import { useState } from 'react';
import { getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '@/config/constants';
import {
  SparklesIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface AIWritingAssistantProps {
  onContentGenerated: (
    content: string,
    type: 'title' | 'content' | 'excerpt' | 'seo'
  ) => void;
  postData?: {
    title?: string;
    category?: string;
    location?: string;
    keywords?: string[];
  };
  disabled?: boolean;
}

interface GenerationRequest {
  prompt: string;
  type: 'title' | 'content' | 'excerpt' | 'seo';
  context?: {
    title?: string;
    category?: string;
    location?: string;
    keywords?: string[];
  };
}

const AIWritingAssistant = ({
  onContentGenerated,
  postData,
  disabled = false,
}: AIWritingAssistantProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [selectedType, setSelectedType] = useState<
    'title' | 'content' | 'excerpt' | 'seo'
  >('content');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const generationTypes = [
    {
      id: 'title' as const,
      name: 'Blog Title',
      icon: DocumentTextIcon,
      description: 'Generate SEO-friendly blog titles',
      placeholder: 'e.g., "Guide to React hooks" or "Best coffee shops in NYC"',
    },
    {
      id: 'content' as const,
      name: 'Blog Content',
      icon: DocumentTextIcon,
      description: 'Generate full blog post content',
      placeholder:
        'e.g., "Write about JavaScript best practices" or "Top 10 productivity tips"',
    },
    {
      id: 'excerpt' as const,
      name: 'Excerpt',
      icon: DocumentTextIcon,
      description: 'Generate compelling excerpts',
      placeholder: 'e.g., "Create an excerpt for my tech tutorial"',
    },
    {
      id: 'seo' as const,
      name: 'SEO Description',
      icon: MagnifyingGlassIcon,
      description: 'Generate meta descriptions',
      placeholder: 'e.g., "SEO description for my food review post"',
    },
  ];

  const generateContent = async () => {
    if (!prompt.trim()) {
      setMessage({ type: 'error', text: 'Please enter a prompt' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const token = getAuthToken();
      const requestData: GenerationRequest = {
        prompt: prompt.trim(),
        type: selectedType,
        context: postData,
      };

      const response = await fetch(`${API_ENDPOINTS.ai}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();

      if (result.success) {
        onContentGenerated(result.data.content, selectedType);
        setMessage({
          type: 'success',
          text: `✨ Generated ${result.data.wordCount} words successfully!`,
        });
        setPrompt('');
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Generation failed',
        });
      }
    } catch (error) {
      console.error('AI generation error:', error);
      setMessage({ type: 'error', text: 'Failed to generate content' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      generateContent();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        disabled={disabled}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-50 group disabled:opacity-50 disabled:cursor-not-allowed"
        title="AI Writing Assistant"
      >
        <SparklesIcon className="h-6 w-6" />
        <span className="absolute -top-2 -left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
          AI
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <SparklesIcon className="h-5 w-5" />
            <h3 className="font-semibold">AI Writing Assistant</h3>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white hover:text-gray-200 transition-colors"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Message */}
        {message && (
          <div
            className={`p-3 rounded-lg flex items-center space-x-2 text-sm ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
            ) : (
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Content Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {generationTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                className={`p-2 text-left rounded-lg border text-sm transition-colors ${
                  selectedType === type.id
                    ? 'border-purple-300 bg-purple-50 text-purple-800'
                    : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-1">
                  <type.icon className="h-4 w-4" />
                  <span className="font-medium">{type.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Prompt Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            What would you like to generate?
          </label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={
              generationTypes.find(t => t.id === selectedType)?.placeholder
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600 text-sm resize-none"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">
            Press Cmd/Ctrl + Enter to generate
          </p>
        </div>

        {/* Context Info */}
        {postData && (
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs font-medium text-gray-700 mb-1">
              Context Available:
            </p>
            <div className="space-y-1 text-xs text-gray-600">
              {postData.title && <p>• Title: {postData.title}</p>}
              {postData.category && <p>• Category: {postData.category}</p>}
              {postData.location && <p>• Location: {postData.location}</p>}
            </div>
          </div>
        )}

        {/* Generate Button */}
        <button
          onClick={generateContent}
          disabled={loading || !prompt.trim()}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-4 rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Generating...</span>
            </>
          ) : (
            <>
              <SparklesIcon className="h-4 w-4" />
              <span>Generate with AI</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AIWritingAssistant;
